/**
 * Deterministic CryoVault fixtures for the visual-evidence harness and local
 * review of the real `/care/cryo-vault` route. Every entry point here is
 * behind `__DEV__` plus an explicit `dev_cryo_vault_*` query parameter, so an
 * exported production bundle never reaches this data and the screen renders
 * from the API alone. Keep fixture data in this module, never in the screen.
 */
import type { CryoSpecimenDetail } from "../components/CryoSpecimenDetailModal";
import type { SpecimenPrefill } from "../screens/care/modals/AddSpecimenModal";

export type DevCryoVaultScenario = "oocyte_batches" | "specimen_modal";

export function getDevCryoVaultScenario(): DevCryoVaultScenario | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const scenario = params.get("dev_cryo_vault_scenario");
    return scenario === "oocyte_batches" || scenario === "specimen_modal"
      ? scenario
      : null;
  } catch {
    return null;
  }
}

export function shouldOpenDevAddEmbryosSurface(): boolean {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get("dev_cryo_vault_open") ===
      "add_embryos"
    );
  } catch {
    return false;
  }
}

export function buildDevCryoVaultSpecimens(
  labelBuilder: (apiType: string, index: number) => string,
): CryoSpecimenDetail[] {
  const makeOocyteCohort = (
    index: number,
    facility: string,
    frozenDate: string,
    batchId: string,
    numberFrozen: number,
    numberThawed = 0,
  ): CryoSpecimenDetail => ({
    id: `dev-oocyte-cohort-${index}`,
    type: "oocyte",
    label: labelBuilder("oocyte_cohort", index),
    oocyteRecordKind: "cohort",
    facility,
    status: "stored",
    batchId,
    cycleId: batchId,
    frozenDate,
    notes: null,
    source: "self",
    numberFrozen,
    numberThawed,
  });

  return [
    makeOocyteCohort(
      1,
      "Kindbody San Francisco",
      "2026-02-12",
      "dev-cycle-eggs-2026-02",
      4,
    ),
    makeOocyteCohort(
      2,
      "NY Cryo",
      "2025-09-18",
      "dev-cycle-eggs-2025-09",
      3,
      2,
    ),
    makeOocyteCohort(3, "NY Cryo", "2024-04-08", "dev-cycle-eggs-2024-04", 2),
    {
      id: "dev-embryo-1",
      type: "embryo",
      label: labelBuilder("embryo", 1),
      facility: "NY Cryo",
      batchId: "dev-cycle-embryos-2025-09",
      cycleId: "dev-cycle-embryos-2025-09",
      frozenDate: "2025-09-24",
      notes: null,
      grade: "4AA",
      pgtResult: "euploid",
      transferCandidate: true,
      fertilizationDate: "2025-09-19",
      fertilizationMethod: "ICSI",
      sourceEggId: "dev-oocyte-cohort-2",
      sourceEggSource: "self",
    },
    {
      id: "dev-embryo-2",
      type: "embryo",
      label: labelBuilder("embryo", 2),
      facility: "NY Cryo",
      batchId: "dev-cycle-embryos-2025-09",
      cycleId: "dev-cycle-embryos-2025-09",
      frozenDate: "2025-09-24",
      notes: null,
      grade: "4BB",
      pgtResult: "untested",
      fertilizationDate: "2025-09-19",
      fertilizationMethod: "ICSI",
      sourceEggId: "dev-oocyte-cohort-2",
      sourceEggSource: "self",
    },
    {
      id: "dev-sperm-1",
      type: "sperm",
      label: labelBuilder("semen", 1),
      facility: "Kindbody San Francisco",
      batchId: "dev-sperm-2026-01",
      cycleId: null,
      frozenDate: "2026-01-21",
      notes: null,
      motility: "62%",
      concentration: "48M/mL",
      morphology: "5%",
      source: "self",
    },
  ];
}

export function buildDevSpecimenModalSpecimens(
  labelBuilder: (apiType: string, index: number) => string,
): CryoSpecimenDetail[] {
  const specimens: CryoSpecimenDetail[] = [
    {
      id: "dev-specimen-oocyte-1",
      type: "oocyte",
      label: labelBuilder("oocyte_cohort", 1),
      oocyteRecordKind: "cohort",
      cohortMemberSpecimenIds: Array.from(
        { length: 10 },
        (_, index) => `dev-specimen-oocyte-1-member-${index + 1}`,
      ),
      facility: "NY Cryo",
      status: "stored",
      collectedAt: "2026-03-15",
      batchId: "dev-cycle-ivf-2026-03",
      cycleId: "dev-cycle-ivf-2026-03",
      cycleLabel: "NY Fertility Center · Cycle #2 · Mar 2026 IVF",
      frozenDate: "2026-03-15",
      storageId: "Tank 4, Canister B, Cane 13",
      storageLocationType: "cryobank",
      storageVessel: "straw",
      tankNumber: "4",
      canisterNumber: "B",
      caneNumber: "13",
      strawColor: "blue",
      strawNumber: "2",
      country: "US",
      source: "self",
      retrievalDate: "2026-03-15",
      freezingMethod: "vitrification",
      grade: "mii",
      totalRetrieved: 12,
      matureMII: 10,
      immatureMI: 1,
      degenerated: 1,
      numberFrozen: 10,
      numberThawed: 0,
      postThawSurvivalRate: null,
      predictiveReportName: "Future fertility report",
      predictiveReportProvider: "Ovation Analytics",
      predictiveReportDate: "2026-03-22",
      predictedEggQuality: "At least 1 blastocyst: 84%",
      predictedFertilityOutlook: "At least 1 live birth: 42%",
      predictiveReportAssumptions:
        "Based on 10 mature oocytes, oocyte images, age at retrieval, expected thaw survival, euploidy modelling, and comparison to age/count calculators.",
      notes:
        "Fixture egg for reviewing the specimen modal shell and cycle link treatment.",
    },
    {
      id: "dev-specimen-oocyte-2",
      type: "oocyte",
      label: labelBuilder("oocyte_cohort", 2),
      oocyteRecordKind: "cohort",
      cohortMemberSpecimenIds: Array.from(
        { length: 11 },
        (_, index) => `dev-specimen-oocyte-2-member-${index + 1}`,
      ),
      facility: "NY Cryo",
      status: "partially_used",
      collectedAt: "2024-09-10",
      batchId: "dev-cycle-egg-freeze-2024-09",
      cycleId: "dev-cycle-egg-freeze-2024-09",
      cycleLabel: "Kindbody · Cycle #1 · Sep 2024 egg freezing",
      useCycleId: "dev-cycle-thaw-icsi-2026-05",
      useCycleLabel: "Spring Fertility · Cycle #3 · May 2026 IVF/ICSI",
      frozenDate: "2024-09-10",
      storageId: "Tank 2, Canister D, Cane 4",
      storageLocationType: "cryobank",
      storageVessel: "straw",
      tankNumber: "2",
      canisterNumber: "D",
      caneNumber: "4",
      strawColor: "white",
      strawNumber: "6",
      country: "US",
      source: "self",
      retrievalDate: "2024-09-10",
      freezingMethod: "vitrification",
      grade: "mii",
      totalRetrieved: 14,
      matureMII: 11,
      immatureMI: 2,
      degenerated: 1,
      numberFrozen: 11,
      numberThawed: 4,
      fertilizedCount: 3,
      postThawSurvivalRate: 100,
      predictiveReportName: "Future fertility report",
      predictiveReportProvider: "Ovation Analytics",
      predictiveReportDate: "2024-09-18",
      predictedEggQuality: "At least 2 blastocysts: 68%",
      predictedFertilityOutlook: "At least 1 live birth: 35%",
      predictiveReportAssumptions:
        "Based on 11 mature oocytes frozen in 2024 and later thaw outcomes from the May 2026 ICSI cycle.",
      notes:
        "Fixture cohort showing frozen eggs later thawed, fertilized, and linked to embryos.",
    },
    {
      id: "dev-specimen-semen-1",
      type: "sperm",
      label: labelBuilder("semen", 1),
      facility: "NY Cryo",
      status: "stored",
      recordSource: "patient",
      ownership: "patient",
      collectedAt: "2026-03-14",
      batchId: "dev-cycle-ivf-2026-03",
      cycleId: "dev-cycle-ivf-2026-03",
      cycleLabel: "NY Fertility Center · Cycle #2 · Mar 2026 IVF",
      frozenDate: "2026-03-14",
      storageId: "Tank 8, Canister C, Straw 7",
      storageLocationType: "cryobank",
      storageVessel: "straw",
      tankNumber: "8",
      canisterNumber: "C",
      strawColor: "green",
      strawNumber: "7",
      country: "US",
      source: "self",
      collectionMethod: "ejaculation",
      processingMethod: "frozen",
      preparationMethod: "simple_wash",
      sampleId: "SEM-2026-001",
      abstinencePeriod: 3,
      analysisDate: "2026-03-14",
      volume: "2.8 mL",
      totalCount: "82",
      concentration: "42 M/mL",
      motility: "58%",
      progressiveMotility: "52%",
      morphology: "6% normal",
      tmsc: "47.2",
      ph: "7.6",
      viscosity: "1",
      notes:
        "Fixture semen sample for reviewing evidence-backed semen values and missing source documents.",
    },
    {
      id: "dev-specimen-embryo-1",
      type: "embryo",
      label: labelBuilder("embryo", 1),
      facility: "NY Cryo",
      status: "stored",
      embryoStatus: "stored",
      collectedAt: "2026-03-16",
      batchId: "dev-cycle-ivf-2026-03",
      cycleId: "dev-cycle-ivf-2026-03",
      cycleLabel: "NY Fertility Center · Cycle #2 · Mar 2026 IVF",
      frozenDate: "2026-03-21",
      storageId: "Tank 4, Canister B, Cane 12",
      storageLocationType: "cryobank",
      storageVessel: "straw",
      tankNumber: "4",
      canisterNumber: "B",
      caneNumber: "12",
      strawColor: "blue",
      strawNumber: "7",
      country: "US",
      sourceEggId: "dev-specimen-oocyte-1",
      sourceEggSource: "self",
      sourceSpermId: "dev-specimen-semen-1",
      sourceSpermSource: "self",
      embryoId: "E-4AA",
      embryoSex: "xx",
      grade: "4AA",
      pgtResult: "euploid",
      pgtPerformed: "PGT-A",
      transferCandidate: true,
      fertilizationDate: "2026-03-16",
      fertilizationMethod: "ICSI",
      dayOfDevelopment: "5",
      pgtReportDate: "2026-03-20",
      pgtReportSummary: "Euploid embryo, female embryo sex reported by lab.",
      notes:
        "Fixture embryo for reviewing quality, created-from, and transfer action treatment.",
    },
    {
      id: "dev-specimen-embryo-2",
      type: "embryo",
      label: labelBuilder("embryo", 2),
      facility: "NY Cryo",
      status: "stored",
      embryoStatus: "stored",
      collectedAt: "2026-05-12",
      batchId: "dev-cycle-thaw-icsi-2026-05",
      cycleId: "dev-cycle-thaw-icsi-2026-05",
      cycleLabel: "Spring Fertility · Cycle #3 · May 2026 IVF/ICSI",
      frozenDate: "2026-05-17",
      storageId: "Tank 2, Canister D, Cane 5",
      storageLocationType: "cryobank",
      storageVessel: "straw",
      tankNumber: "2",
      canisterNumber: "D",
      caneNumber: "5",
      strawColor: "white",
      strawNumber: "8",
      country: "US",
      sourceEggId: "dev-specimen-oocyte-2",
      sourceEggSource: "self",
      sourceSpermId: "dev-specimen-semen-1",
      sourceSpermSource: "self",
      embryoId: "E-5AB",
      embryoSex: "xy",
      grade: "5AB",
      pgtResult: "euploid",
      pgtPerformed: "PGT-A",
      transferCandidate: true,
      fertilizationDate: "2026-05-12",
      fertilizationMethod: "ICSI",
      dayOfDevelopment: "5",
      pgtReportDate: "2026-05-18",
      pgtReportSummary: "Euploid embryo created from thawed 2024 frozen eggs.",
      notes:
        "Fixture embryo created from the thawed oocyte cohort and still stored.",
    },
    {
      id: "dev-specimen-embryo-3",
      type: "embryo",
      label: labelBuilder("embryo", 3),
      facility: "NY Cryo",
      status: "transferred",
      embryoStatus: "transferred",
      collectedAt: "2026-05-12",
      batchId: "dev-cycle-thaw-icsi-2026-05",
      cycleId: "dev-cycle-thaw-icsi-2026-05",
      cycleLabel: "Spring Fertility · Cycle #3 · May 2026 IVF/ICSI",
      transferCycleId: "dev-cycle-fet-2026-07",
      transferCycleLabel: "FET #1 | Jul 2026",
      frozenDate: "2026-05-17",
      storageId: "Previously Tank 2, Canister D, Cane 5",
      storageLocationType: "cryobank",
      storageVessel: "straw",
      tankNumber: "2",
      canisterNumber: "D",
      caneNumber: "5",
      strawColor: "white",
      strawNumber: "9",
      country: "US",
      sourceEggId: "dev-specimen-oocyte-2",
      sourceEggSource: "self",
      sourceSpermId: "dev-specimen-semen-1",
      sourceSpermSource: "self",
      embryoId: "E-5AA",
      embryoSex: "xx",
      grade: "5AA",
      pgtResult: "euploid",
      pgtPerformed: "PGT-A",
      transferCandidate: false,
      fertilizationDate: "2026-05-12",
      fertilizationMethod: "ICSI",
      dayOfDevelopment: "5",
      pgtReportDate: "2026-05-18",
      pgtReportSummary:
        "Euploid embryo created from thawed eggs and linked to a later FET.",
      notes: "Fixture embryo showing a downstream frozen embryo transfer link.",
    },
  ];
  // Evidence-only absence variant of the existing review specimen. Normal
  // navigation and production continue to use API data, never this fixture.
  if (
    __DEV__ &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get(
      "dev_cryo_vault_analysis",
    ) === "empty"
  ) {
    return specimens.map((specimen) =>
      specimen.type === "sperm"
        ? {
            ...specimen,
            volume: null,
            concentration: null,
            totalCount: null,
            tmsc: null,
            motility: null,
            progressiveMotility: null,
            nonProgressiveMotility: null,
            immotile: null,
            morphology: null,
            agglutinationAggregation: null,
            ph: null,
            viscosity: null,
          }
        : specimen,
    );
  }
  return specimens;
}

/** Prefill for the `dev_cryo_vault_open=add_embryos` review route only. */
export function getDevAddEmbryosPrefill(): SpecimenPrefill | undefined {
  if (!shouldOpenDevAddEmbryosSurface()) return undefined;
  return {
    type: "embryos",
    clinicName: "Spring Fertility — East Bay",
    retrievalDate: "2026-06-12",
    freezeDate: "2026-06-15",
    numberFrozen: 5,
    fertilizationMethod: "icsi",
    embryoDayOfDevelopment: "5",
    storageLocation: "Tank 4, Canister B, Cane 13",
  };
}
